import { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useNdas, useSignNda } from "@/hooks/useNda";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Spinner } from "@/components/common";

export interface NdaSignModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: string;
  entityId: string;
  onSuccess: () => void;
  title?: string;
  description?: string;
}

export function NdaSignModal({
  isOpen,
  onClose,
  entityType,
  entityId,
  onSuccess,
  title = "Thỏa thuận bảo mật (NDA)",
  description = "Bạn cần ký Thỏa thuận bảo mật thông tin (NDA) để xem chi tiết và trao đổi.",
}: NdaSignModalProps) {
  const { data: ndaTemplates, isLoading: loadingNdas } = useNdas();
  const [selectedNdaId, setSelectedNdaId] = useState<string>("");

  const selectedNda = Array.isArray(ndaTemplates)
    ? ndaTemplates.find((n: any) => n.id === selectedNdaId) || ndaTemplates[0]
    : null;

  const { mutate: signNda, isPending: isSigningNda } = useSignNda();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-[2px] animate-in fade-in duration-200">
      <div className="bg-white rounded-lg w-full max-w-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
        <div className="bg-orange-600 px-6 py-4 flex justify-between items-center text-white shrink-0">
          <h3 className="text-lg font-bold">{title}</h3>
          <button onClick={onClose}><ArrowLeft size={22} /></button>
        </div>
        <div className="p-6 overflow-y-auto flex-1 text-left">
          {loadingNdas ? (
            <div className="flex justify-center py-10"><Spinner /></div>
          ) : (
            <>
              <div className="mb-6">
                <Select
                  label="Chọn mẫu NDA"
                  options={Array.isArray(ndaTemplates) ? ndaTemplates.map((nda: any) => ({ value: nda.id, label: nda.name })) : []}
                  value={selectedNdaId || (ndaTemplates && ndaTemplates[0]?.id) || ""}
                  onChange={(e) => setSelectedNdaId(e.target.value)}
                />
              </div>
              {selectedNda ? (
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap p-4 bg-gray-50 rounded-lg border">
                  {selectedNda.content}
                </div>
              ) : (
                <div className="text-center py-10 text-gray-500">
                  <p className="mb-4">{description}</p>
                </div>
              )}
              <div className="text-center py-6 text-gray-500 mt-4 border-t">
                <p className="mb-2">{description}</p>
                <p className="text-sm">Bằng việc nhấn "Tôi đồng ý & Ký", bạn cam kết không tiết lộ các thông tin mật của doanh nghiệp này cho bên thứ ba.</p>
              </div>
            </>
          )}
        </div>
        <div className="p-6 pt-4 border-t border-gray-100 flex gap-3 shrink-0 bg-gray-50">
          <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
            Hủy
          </Button>
          <Button
            type="button"
            variant="brand"
            className="flex-1"
            loading={isSigningNda}
            onClick={() => {
              const finalNdaId = selectedNdaId || (ndaTemplates && ndaTemplates[0]?.id) || "default";
              signNda({
                entityType,
                entityId,
                ndaId: finalNdaId,
              }, {
                onSuccess: () => {
                  onSuccess();
                }
              });
            }}
          >
            Tôi đồng ý & Ký
          </Button>
        </div>
      </div>
    </div>
  );
}
